import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Autocomplete,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

function AssignEmployeeDialog({ t, open, handleClose, setMaintenance, maintenance }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [primaryEmployeeId, setPrimaryEmployeeId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedEmployees([]);
      setPrimaryEmployeeId(null);
      setError(null);

      const loadEmployees = async () => {
        setLoading(true);
        try {
          const response = await axios.get('/api/employees/');
          // Handle paginated response
          const employeesData = response.data.results || response.data;
          const employeesList = Array.isArray(employeesData) ? employeesData : [];
          setEmployees(employeesList);

          // Pre-select current employees if exist
          if (maintenance?.employees && maintenance.employees.length > 0) {
            const currentIds = maintenance.employees.map((e) => e.id);
            const currentEmployees = employeesList.filter((e) => currentIds.includes(e.id));
            setSelectedEmployees(currentEmployees);

            // Find primary employee
            const primaryEmp = maintenance.employees.find((e) => e.is_primary);
            if (primaryEmp) {
              setPrimaryEmployeeId(primaryEmp.id);
            } else if (currentEmployees.length > 0) {
              setPrimaryEmployeeId(currentEmployees[0].id);
            }
          }
        } catch (err) {
          console.error('Error loading employees:', err);
        } finally {
          setLoading(false);
        }
      };
      loadEmployees();
    }
  }, [open, maintenance?.employees]);

  const handleEmployeesChange = (e, value) => {
    setSelectedEmployees(value);

    // Update primary if needed
    const newIds = value.map((e) => e.id);
    if (!newIds.includes(primaryEmployeeId)) {
      setPrimaryEmployeeId(newIds[0] || null);
    }
  };

  const handleSetPrimary = (employeeId, event) => {
    event.stopPropagation();
    setPrimaryEmployeeId(employeeId);
  };

  const handleAssign = async () => {
    setSubmitting(true);
    try {
      const response = await axios.patch(`/api/maintenances/${maintenance.id}/set_employees/`, {
        employee_ids: selectedEmployees.map((e) => e.id),
        primary_employee_id: primaryEmployeeId,
      });
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('Assign mechanics')}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'text.disabled' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 2 }}>
        <Autocomplete
          multiple
          options={employees}
          getOptionLabel={(option) => option?.name || ''}
          loading={loading}
          value={selectedEmployees}
          onChange={handleEmployeesChange}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {option.name}
                </Typography>
                {(option.title || option.role) && (
                  <Typography variant="caption" color="text.secondary">
                    {option.title || t(`role_${option.role}`)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const isPrimary = primaryEmployeeId === option.id;
              return (
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{option.name}</span>
                      {value.length > 1 && (
                        <Box
                          onClick={(e) => handleSetPrimary(option.id, e)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            ml: 0.5,
                          }}
                        >
                          {isPrimary ? (
                            <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          ) : (
                            <StarBorderIcon
                              sx={{
                                fontSize: 16,
                                color: 'text.disabled',
                                '&:hover': { color: 'warning.main' },
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                  {...getTagProps({ index })}
                  key={option.id}
                  sx={{
                    borderRadius: '8px',
                    bgcolor: isPrimary ? 'rgba(237, 137, 54, 0.15)' : undefined,
                    '& .MuiChip-label': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              autoComplete="off"
              label={t('Select mechanics')}
              placeholder={selectedEmployees.length === 0 ? t('Search mechanics...') : ''}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
          )}
        />

        {selectedEmployees.length > 1 && (
          <Typography
            sx={{
              fontSize: '12px',
              color: 'text.secondary',
              mt: 1.5,
            }}
          >
            {t('Click star to set as lead mechanic')}
          </Typography>
        )}

        {error && (
          <Typography color="error" sx={{ mt: 2, fontSize: '14px' }}>
            {error}
          </Typography>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ borderRadius: '10px', px: 3 }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={submitting}
            sx={{
              borderRadius: '10px',
              px: 3,
              bgcolor: 'secondary.main',
              '&:hover': { bgcolor: 'secondary.light' },
            }}
          >
            {submitting ? <CircularProgress size={20} /> : <span>{t('Save')}</span>}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default withTranslation()(AssignEmployeeDialog);
